import React, { useEffect, useRef, useState } from 'react';
import { Send, Loader2, Lock } from 'lucide-react';
import { cn } from '@/lib/utils';
import { composerValue, replyLength } from '../../lib/chatsView';

/*
 * r16p (design 7.1, R23): the reply box. AssistantComposer's behaviour (Enter sends, the IME guard) with:
 *   - no attachments (r16p sends text only);
 *   - NO NEWLINES: Shift+Enter inserts nothing, and pasted or typed line breaks become one space with a one-line hint - newlines are
 *     not sent until a harness and a live check prove them on the phone's typing paths (design 6.5). The queued bubble then shows
 *     the server's normalised text;
 *   - maxLength from the server's info (default 1,000), counted as the server counts it (code points after normalising), with a
 *     counter from 80 %;
 *   - closed with the server's reason in words when replies are not possible (replies_allowed=false, PREVIEW mode, TikTok...).
 * The value lives in the caller (lib/chatDraft.js, memory only, one draft per conversation).
 */
export default function ReplyComposer({
  value, onChange, onSend, disabled = false, disabledReason = null, busy = false, maxLength = 1000, newlines = false, inputRef,
  onHeight, hint = null, testId = 'reply-input',
}) {
  const formRef = useRef(null);
  const localRef = useRef(null);
  const boxRef = inputRef || localRef;
  const [breakHint, setBreakHint] = useState(false);
  const len = replyLength(value || '');
  const over = len > maxLength;
  const locked = disabled || busy;

  useEffect(() => {
    if (!onHeight || !formRef.current) return undefined;
    const el = formRef.current;
    const report = () => onHeight(el.getBoundingClientRect().height || el.offsetHeight || 0);
    report();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(report);
    ro.observe(el);
    return () => ro.disconnect();
  }, [onHeight, disabled]);

  // grows with the text up to max-h-40 (10rem), then scrolls inside
  useEffect(() => {
    const el = boxRef.current;
    if (!el || !el.style) return;
    el.style.height = 'auto';
    const h = el.scrollHeight;
    if (h) el.style.height = `${Math.min(h, 160)}px`;
  }, [value, boxRef]);

  const set = (raw) => {
    const v = composerValue(raw, newlines);
    if (v.hadBreak) setBreakHint(true);
    onChange(v.text);
  };
  const submit = (e) => {
    if (e) e.preventDefault();
    if (locked || over || !String(value || '').trim()) return;
    onSend();
  };

  if (disabled && disabledReason) {
    return (
      <div ref={formRef} className="border-t border-border/70 p-3" data-testid={`${testId}-closed`}>
        <p className="flex items-start gap-2 rounded-xl bg-secondary/50 px-3 py-2.5 text-sm text-muted-foreground">
          <Lock className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
          <span>{disabledReason}</span>
        </p>
      </div>
    );
  }

  return (
    <form ref={formRef} onSubmit={submit} className="border-t border-border/70 p-3" data-testid={`${testId}-form`}>
      <div className="flex items-end gap-2">
        <textarea
          ref={boxRef}
          rows={1}
          value={value || ''}
          onChange={(e) => set(e.target.value)}
          onKeyDown={(e) => {
            if (e.key !== 'Enter' || e.nativeEvent.isComposing || e.keyCode === 229) return;
            // Enter sends; Shift+Enter would be a new line, which r16p does not send - it inserts nothing
            e.preventDefault();
            if (e.shiftKey && !newlines) { setBreakHint(true); return; }
            if (!e.shiftKey) submit();
          }}
          disabled={locked}
          placeholder={disabled ? 'Replies are closed here' : 'Write a reply…'}
          aria-label="Reply"
          data-testid={testId}
          className="max-h-40 min-h-[44px] min-w-0 flex-1 resize-none rounded-xl border border-border bg-secondary/40 px-3 py-2.5 text-sm text-foreground outline-none placeholder:text-muted-foreground/70 focus:ring-2 focus:ring-ring disabled:opacity-60"
        />
        <button
          type="submit"
          aria-label="Send reply"
          data-testid={`${testId}-send`}
          disabled={locked || over || !String(value || '').trim()}
          className="tap inline-flex shrink-0 items-center justify-center rounded-xl bg-primary p-3 text-primary-foreground transition-opacity disabled:opacity-40"
        >
          {busy ? <Loader2 className="h-[18px] w-[18px] animate-spin" aria-hidden /> : <Send className="h-[18px] w-[18px]" aria-hidden />}
        </button>
      </div>
      <div className="mt-1 flex flex-wrap items-center justify-between gap-x-3 gap-y-0.5 text-[11px] text-muted-foreground">
        <span>{hint || 'Enter to send · sent by the phone at the slot\'s next session, not in real time'}</span>
        {len > maxLength * 0.8 && (
          <span className={cn('tnum', over && 'font-semibold text-danger')} data-testid={`${testId}-count`}>{len} / {maxLength}</span>
        )}
      </div>
      {breakHint && !newlines && (
        <p className="mt-0.5 text-[11px] text-warning" role="status" data-testid={`${testId}-nobreaks`}>
          Line breaks are not sent yet, so they became spaces.
        </p>
      )}
    </form>
  );
}
